import React, { useState } from 'react';
import logo from 'assets/images/emessage-logo.png';
import * as yup from 'yup';
import { each } from 'lodash';
import { useFormik } from 'formik';
import { Envelope, Info } from '@phosphor-icons/react';
import Form from 'partial/components/form';
import Button from 'partial/components/Button';
import FormInput from 'partial/components/form/FormInput';
import FormPassword from 'partial/components/form/FormPassword';
import { useLogin } from '../hooks';
import { useConfirmMfaOtp } from '../modals/ConfirmMFA';
import { useMFA } from '../modals/SetupMFA';

const initialValues = {
  email: '',
  password: '',
};

const validationSchema = yup.object().shape({
  email: yup.string().email('Invalid').required('Required'),
  password: yup.string().required('Required'),
});

/*
 * One 420px panel centred on the neutral-50 field. The polygon-network hero
 * photograph is dropped — no hero, no gradient, no shadow. The dark wordmark
 * sits over the form; the white cut is unused here.
 *
 * Exactly two fields and one primary action. No SSO, no forgot-password —
 * neither exists and the flow is frozen.
 */
function Landing() {
  const [isLoading, login] = useLogin();
  const setupMFA = useMFA();
  const openMfa = useConfirmMfaOtp();
  const [validateOnChange, setValidateOnChange] = useState(false);
  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (form, { setFieldError }) => {
      setValidateOnChange(true);
      login(
        form,
        () => {}, // onSuccess
        (err) => {
          each(err, (v, k) => {
            setFieldError(k, v);
          });
        },
        () => openMfa(form.email),
        (cb) => setupMFA(cb),
      );
    },
    validateOnChange,
  });

  return (
    <div className="min-h-screen w-full bg-neutral-50 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-[420px] bg-white border border-neutral-200 rounded-lg p-8">
        <img src={logo} alt="eMessage Core" className="h-9 w-auto" />

        <h1 className="mt-6 text-2xl font-bold tracking-[-.015em] text-neutral-900">
          Sign in
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Use the credentials issued by your agency administrator.
        </p>

        <Form onSubmit={handleSubmit} error={errors} noValidate>
          <div className="mt-6 space-y-4">
            <FormInput
              icon={Envelope}
              name="email"
              onSetFieldValue={setFieldValue}
              value={values.email}
              type="email"
              error={errors.email}
              label="Email address"
              placeholder="you@agency.gov.ph"
            />
            <FormPassword
              name="password"
              label="Password"
              onSetFieldValue={setFieldValue}
              value={values.password}
              error={errors.password}
              noAutoFill
            />
            <Button
              primary
              type="submit"
              className="w-full"
              isLoading={isLoading}
              loadingLabel="Signing in…"
            >
              Sign in
            </Button>
          </div>
        </Form>

        <div className="mt-6 pt-4 border-t border-neutral-200 flex gap-2">
          <Info
            size={16}
            className="text-neutral-400 flex-shrink-0 mt-px"
            aria-hidden="true"
          />
          <p className="text-xs text-neutral-500">
            Access is monitored. All sign-in attempts are recorded in the agency
            audit log.
          </p>
        </div>
      </div>

      <p className="mt-6 text-xs text-neutral-500 tabular-nums">
        eMessage Core · Administrator Portal · v2.0.0
      </p>
    </div>
  );
}

export default Landing;
